import { ReactNode, useRef } from 'react';
import { Link } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { useTextLineCount } from '@libs/hooks/useTextLineCount';
import { useMediaQuery } from '@providers/MediaQueryProvider';

import { ClubLogo } from '@features/club/ui/ClubLogo';
import { MemberClubDto, Tournament } from '@features/shared/api/typings';
import { useSubheaderMediaQuery } from '@features/shared/hooks/useSubheaderMediaQuery';
import { TournamentSponsor } from '@features/tournament/ui/components/TournamentSponsor/TournamentSponsor';
import { TournamentStartDate } from '@features/tournament/ui/components/TournamentStartDate/TournamentStartDate';
import { TournamentStatusView } from '@features/tournament/ui/components/TournamentStatusView';
import { TournamentBadges } from '@features/tournament/ui/components/TournamentSubHeader/TournamentBadges';

import { TournamentPromoBadge } from './badges/TournamentPromoBadge/TournamentPromoBadge';

import styles from './TournamentSubHeader.module.css';

type Props = {
  club?: MemberClubDto;
  tournament?: Tournament;
  actions?: ReactNode;
  isManager?: boolean;
  isAdmin?: boolean;
  isRcfReportAvailable: boolean;
  rcfReportError?: string;
  onRcfReportClick: () => void;
};
export function TournamentSubHeader({
  club,
  tournament,
  isManager,
  isAdmin,
  actions,
  isRcfReportAvailable,
  rcfReportError,
  onRcfReportClick,
}: Props) {
  const isMobile = useMediaQuery() === 'mobile';
  const titleRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const { device } = useSubheaderMediaQuery();
  const titleLines = useTextLineCount(titleRef);
  const badgeLines = useTextLineCount(badgesRef);
  const dateLines = useTextLineCount(dateRef);

  if (!club || !tournament) return null;

  const logoUrl = tournament.logoUrl || club.logoUrl;

  const logo = (
    <Link className={styles.clubLogoWrapper} to="/club/$clubId" params={{ clubId: club.clubId }}>
      <ClubLogo className={styles.clubLogo} logoUrl={logoUrl} />
      {club.title}
    </Link>
  );

  const isDraft = tournament.status === 'Draft';
  const draftBadge = isDraft ? '[Черновик] ' : '';

  const sponsor = tournament.sponsors?.[0];

  const isSpecialDate = isDraft || tournament.status === 'NotStarted';
  const { paymentSettings } = tournament;
  const promoBadge =
    paymentSettings?.promoTitle || paymentSettings?.promoLink ? (
      <TournamentPromoBadge
        className={styles.promoBadge}
        title={paymentSettings?.promoTitle}
        link={paymentSettings?.promoLink}
      />
    ) : undefined;

  return (
    <div
      className={cx(styles.subHeader, device, {
        [styles.draft]: isDraft,
      })}
    >
      <div ref={titleRef} className={styles.title}>
        {draftBadge}
        {tournament.title}
        {!isMobile && promoBadge}
      </div>
      <div
        className={cx(styles.separator, {
          [styles.hidden]: isMobile ? titleLines + dateLines > 4 : titleLines + badgeLines > 3,
        })}
      />
      <div className={styles.status} ref={dateRef}>
        {isSpecialDate ? (
          <TournamentStartDate tournament={tournament} />
        ) : (
          <TournamentStatusView tournament={tournament} />
        )}
      </div>
      <div className={styles.logoWithActions}>
        {logo}
        {actions}
      </div>
      <div className={styles.logo}>{logo}</div>
      <div className={styles.actions}>{actions}</div>
      <div className={styles.badges} ref={badgesRef}>
        {isMobile && promoBadge}
        <TournamentBadges
          clubId={club?.clubId}
          tournament={tournament}
          isManager={isManager}
          isAdmin={isAdmin}
          isRcfReportAvailable={isRcfReportAvailable}
          rcfReportError={rcfReportError}
          onRcfReportClick={onRcfReportClick}
        />
      </div>
      {sponsor && (
        <div className={styles.sponsor}>
          <TournamentSponsor
            logoUrl={sponsor.logoUri}
            description={sponsor.caption}
            linkHref={sponsor.linkUri}
          />
        </div>
      )}
    </div>
  );
}
